/**
 * Sound cue library — declarative, procedurally-synthesized SFX.
 *
 * Every cue here is described as data (a list of {@link Voice}s), never a
 * bundled audio file. The {@link SoundEngine} realises a cue with the
 * WebAudio API at play time. This keeps the client asset-free and, more
 * importantly, keeps every sound ORIGINAL: nothing is ripped or copied
 * from another game. The cues are *evocative* of familiar game-audio
 * shapes (an RTS-style rising countdown, a triumphant victory sting, a
 * short "pop" when a queue fires) but each waveform is generated from
 * first principles here, so there is no third-party copyright attached.
 *
 * A cue is a pure value. The engine (impure, owns the AudioContext) is
 * the only place timing/scheduling happens. That split is what makes the
 * cue table unit-testable without any audio backend.
 */

/** Oscillator timbre. Kept to the WebAudio built-ins. */
export type Timbre = "sine" | "square" | "sawtooth" | "triangle";

/**
 * One synthesized voice within a cue. All times are seconds RELATIVE to
 * the cue's start; the engine adds the AudioContext base time.
 *
 * The gain envelope is a simple attack→hold→release triangle: it ramps
 * from 0 to `peak` over `attack`, holds until `release` begins, then
 * ramps back to 0 by `start + duration`. Frequency optionally glides
 * from `freq` to `freqTo` across the voice (linear), which is what gives
 * the countdown its rising "beep" and the whoosh its sweep.
 */
export interface Voice {
  readonly timbre: Timbre;
  /** Start offset within the cue, seconds. */
  readonly start: number;
  /** Total voice length, seconds. */
  readonly duration: number;
  /** Base frequency, Hz. */
  readonly freq: number;
  /** Optional glide target, Hz. Omitted → constant pitch. */
  readonly freqTo?: number;
  /** Peak gain 0..1 (pre master/volume). */
  readonly peak: number;
  /** Attack time, seconds (0..duration). */
  readonly attack: number;
  /** Release time, seconds (0..duration). Overlap with attack is clamped. */
  readonly release: number;
}

/** A named, fully-described sound effect. */
export interface Cue {
  readonly id: CueId;
  readonly voices: readonly Voice[];
}

/** All cue identifiers. Adding a sound = add here + in {@link CUES}. */
export type CueId = "ability" | "victory" | "defeat" | "queuePop" | "countdownTick" | "countdownGo";

/** Total wall-clock length of a cue = max(start + duration) over voices. */
export function cueDurationSeconds(cue: Cue): number {
  let max = 0;
  for (const v of cue.voices) {
    const end = v.start + v.duration;
    if (end > max) max = end;
  }
  return max;
}

// --- Cue definitions -------------------------------------------------------
//
// Frequencies below reference a rough equal-tempered scale (A4 = 440):
//   C5 523  E5 659  G5 784  C6 1047  G4 392  E4 330  C4 262  A3 220
// Chosen by ear to read as "ability zap", "win fanfare", "lose thud",
// "match-found pop", and a rising RTS-style countdown. All original.

/** Ability use: a short bright zap with a downward pitch tail. */
const ABILITY: Cue = {
  id: "ability",
  voices: [
    {
      timbre: "square",
      start: 0,
      duration: 0.12,
      freq: 880,
      freqTo: 440,
      peak: 0.22,
      attack: 0.004,
      release: 0.09,
    },
    {
      timbre: "triangle",
      start: 0,
      duration: 0.14,
      freq: 1320,
      freqTo: 660,
      peak: 0.12,
      attack: 0.004,
      release: 0.11,
    },
  ],
};

/** Victory: a rising three-note major arpeggio (C5 → E5 → G5 → C6). */
const VICTORY: Cue = {
  id: "victory",
  voices: [
    voiceNote("triangle", 0.0, 0.16, 523, 0.28),
    voiceNote("triangle", 0.13, 0.16, 659, 0.28),
    voiceNote("triangle", 0.26, 0.16, 784, 0.28),
    voiceNote("triangle", 0.39, 0.42, 1047, 0.32),
    // A soft square doubling under the final note for a fuller sting.
    voiceNote("square", 0.39, 0.42, 523, 0.12),
  ],
};

/** Defeat: a descending, detuned two-note fall (G4 → C4) with a low body. */
const DEFEAT: Cue = {
  id: "defeat",
  voices: [
    voiceNote("sawtooth", 0.0, 0.24, 392, 0.24),
    voiceNote("sawtooth", 0.2, 0.5, 262, 0.26),
    // Sub thump for weight.
    voiceNote("sine", 0.2, 0.5, 131, 0.28),
  ],
};

/** Queue pop (match found): a quick two-tone "pop" that pings upward. */
const QUEUE_POP: Cue = {
  id: "queuePop",
  voices: [
    {
      timbre: "sine",
      start: 0,
      duration: 0.09,
      freq: 660,
      freqTo: 990,
      peak: 0.3,
      attack: 0.003,
      release: 0.07,
    },
    voiceNote("sine", 0.09, 0.12, 1319, 0.26),
  ],
};

/** Countdown tick: a single crisp blip. Pitch is set per-second by the
 *  controller via {@link countdownTickCue}; this is the "3/2/1" beep. */
const COUNTDOWN_TICK: Cue = {
  id: "countdownTick",
  voices: [voiceNote("square", 0, 0.11, 660, 0.24)],
};

/** Countdown go: the higher, longer "fight!" blip at zero. */
const COUNTDOWN_GO: Cue = {
  id: "countdownGo",
  voices: [voiceNote("square", 0, 0.28, 1047, 0.3), voiceNote("triangle", 0, 0.28, 1568, 0.16)],
};

/** The full cue table, keyed by id. */
export const CUES: Readonly<Record<CueId, Cue>> = {
  ability: ABILITY,
  victory: VICTORY,
  defeat: DEFEAT,
  queuePop: QUEUE_POP,
  countdownTick: COUNTDOWN_TICK,
  countdownGo: COUNTDOWN_GO,
};

/**
 * Build a one-shot countdown-tick cue at a specific pitch. The controller
 * raises the pitch as the count approaches zero (3 low → 1 high), an
 * RTS-flavoured rising countdown built entirely from these tones.
 */
export function countdownTickCue(freq: number): Cue {
  return {
    id: "countdownTick",
    voices: [voiceNote("square", 0, 0.11, freq, 0.24)],
  };
}

/** Convenience builder for a constant-pitch attack/hold/release note. */
function voiceNote(
  timbre: Timbre,
  start: number,
  duration: number,
  freq: number,
  peak: number,
): Voice {
  return {
    timbre,
    start,
    duration,
    freq,
    peak,
    attack: 0.005,
    release: Math.min(0.12, duration * 0.6),
  };
}
